import { useToast, useDisclosure } from '@chakra-ui/react';
import EpisodeCard from 'components/card/EpisodeCard';
import EpisodeCloseModal from 'components/layouts/patients/EpisodeCloseModal';
import EpisodeCreateModal from 'components/layouts/patients/EpisodeCreateModal';
import EpisodeDiagnosticoModal from 'components/layouts/patients/EpisodeDiagnosticoModal';
import EpisodeUpdateModal from 'components/layouts/patients/EpisodeUpdateModal';
import EvaluationDetailModal from 'components/layouts/patients/EvaluationDetailModal';
import EvaluationFormModal from 'components/layouts/patients/EvaluationFormModal';
import TreatmentPlanFormModal from 'components/layouts/patients/TreatmentPlanFormModal';
import { useUpdateEpisode } from 'hooks/useEpisodes';
import { useEvaluationsByEpisode } from 'hooks/useEvaluations';
import { useState } from 'react';
import { ClinicalEpisode, EstadoEpisodio, PhysicalEvaluation, UpdateEpisodioDto } from 'types/models';

interface Props {
  episode: ClinicalEpisode;
  canWrite: boolean;
  isAdmin: boolean;
  headerless?: boolean;
  noBorder?: boolean;
  /** Scale all font sizes: 'sm' (compact), 'md' (default), 'lg' (large) */
  fontSize?: 'sm' | 'md' | 'lg';
  w?: string | number;
  maxW?: string | number;
}

export default function EpisodeCardWithModals({
  episode,
  canWrite,
  isAdmin,
  headerless = false,
  noBorder = false,
  fontSize,
  w,
  maxW,
}: Props) {
  const toast          = useToast();
  const updateEpisode  = useUpdateEpisode();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [completeStep,    setCompleteStep]    = useState(0);
  const [selectedEval,    setSelectedEval]    = useState<PhysicalEvaluation | null>(null);

  const { isOpen: isUpdateOpen,   onOpen: onUpdateOpen,   onClose: onUpdateClose   } = useDisclosure();
  const { isOpen: isCloseOpen,    onOpen: onCloseOpen,    onClose: onCloseClose    } = useDisclosure();
  const { isOpen: isDiagOpen,     onOpen: onDiagOpen,     onClose: onDiagClose     } = useDisclosure();
  const { isOpen: isPlanOpen,     onOpen: onPlanOpen,     onClose: onPlanClose     } = useDisclosure();
  const { isOpen: isCompleteOpen, onOpen: onCompleteOpen, onClose: onCompleteClose } = useDisclosure();
  const { isOpen: isEvalCreateOpen, onOpen: onEvalCreateOpen, onClose: onEvalCreateClose } = useDisclosure();
  const { isOpen: isEvalDetailOpen, onOpen: onEvalDetailOpen, onClose: onEvalDetailClose } = useDisclosure();

  const { data: evaluations = [] } = useEvaluationsByEpisode(episode.pacienteId, episode.id);

  const isActive =
    episode.estado === EstadoEpisodio.ABIERTO ||
    episode.estado === EstadoEpisodio.EN_TRATAMIENTO;

  const canStartTreatment = canWrite && episode.estado === EstadoEpisodio.ABIERTO;
  const canClose          = canWrite && isActive;
  const canEdit           = canWrite && isActive;
  const canArchive        = isAdmin  && episode.estado === EstadoEpisodio.CERRADO;
  const canReopen         = (canWrite || isAdmin) && episode.estado === EstadoEpisodio.CERRADO;
  const canDiagnose       = canWrite && isActive;

  const transition = async (payload: UpdateEpisodioDto, successMsg: string): Promise<boolean> => {
    setIsTransitioning(true);
    try {
      await updateEpisode.mutateAsync({
        patientId: episode.pacienteId,
        episodeId: episode.id,
        payload,
      });
      toast({ title: successMsg, status: 'success', duration: 2500, isClosable: true, position: 'top-right' });
      return true;
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.message ?? 'Intente nuevamente',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      return false;
    } finally {
      setIsTransitioning(false);
    }
  };

  const handleCompleteAtStep = (step: number) => {
    setCompleteStep(step);
    onCompleteOpen();
  };

  return (
    <>
      <EpisodeCard
        episode={episode}
        headerless={headerless}
        noBorder={noBorder}
        fontSize={fontSize}
        w={w}
        maxW={maxW}
        isTransitioning={isTransitioning}
        onDiagnoseClick={canDiagnose ? onDiagOpen : undefined}
        onEditClick={canEdit ? onUpdateOpen : undefined}
        onStartTreatmentClick={
          canStartTreatment
            ? async () => {
                const ok = await transition(
                  { estado: EstadoEpisodio.EN_TRATAMIENTO },
                  'Episodio en tratamiento',
                );
                if (ok) onPlanOpen();
              }
            : undefined
        }
        onCloseClick={canClose ? onCloseOpen : undefined}
        onArchiveClick={
          canArchive
            ? () => transition({ estado: EstadoEpisodio.ARCHIVADO }, 'Episodio archivado')
            : undefined
        }
        onReopenClick={
          canReopen
            ? () => transition({ estado: EstadoEpisodio.ABIERTO }, 'Episodio reabierto')
            : undefined
        }
        onCompleteAtStep={canWrite && isActive ? handleCompleteAtStep : undefined}
        evaluations={evaluations}
        onNewEvaluation={canWrite && isActive ? onEvalCreateOpen : undefined}
        onViewEvaluation={(ev) => { setSelectedEval(ev); onEvalDetailOpen(); }}
      />

      {canDiagnose && (
        <EpisodeDiagnosticoModal isOpen={isDiagOpen} onClose={onDiagClose} episode={episode} />
      )}
      {canEdit && (
        <EpisodeUpdateModal isOpen={isUpdateOpen} onClose={onUpdateClose} episode={episode} />
      )}
      {canClose && (
        <EpisodeCloseModal isOpen={isCloseOpen} onClose={onCloseClose} episode={episode} />
      )}
      <TreatmentPlanFormModal isOpen={isPlanOpen} onClose={onPlanClose} episode={episode} />

      {canWrite && isActive && (
        <EpisodeCreateModal
          isOpen={isCompleteOpen}
          onClose={onCompleteClose}
          patientId={episode.pacienteId}
          existingEpisodeId={episode.id}
          initialStep={completeStep}
        />
      )}

      {canWrite && isActive && (
        <EvaluationFormModal isOpen={isEvalCreateOpen} onClose={onEvalCreateClose} episode={episode} />
      )}

      {selectedEval && (
        <EvaluationDetailModal
          isOpen={isEvalDetailOpen}
          onClose={() => { onEvalDetailClose(); setSelectedEval(null); }}
          evaluation={selectedEval}
          episode={episode}
          canEdit={canWrite && isActive}
        />
      )}
    </>
  );
}
